import React, { useEffect } from 'react';
import { Wrench } from 'lucide-react';
import { CoupleSettings, WebMaintenanceSettings } from '../types';
import { subscribeToSettings } from '../services/firebaseDb';

interface WebMaintenanceScreenProps {
  maintenance?: WebMaintenanceSettings;
  settings?: CoupleSettings;
  isPreview?: boolean;
  onClosePreview?: () => void;
  onMaintenanceEnded?: (updatedSettings: CoupleSettings) => void;
}

export const WebMaintenanceScreen: React.FC<WebMaintenanceScreenProps> = ({
  maintenance,
  isPreview = false,
  onMaintenanceEnded,
}) => {
  // Sincronización en tiempo real: devuelve la web automáticamente al desactivar el mantenimiento
  useEffect(() => {
    if (isPreview) return;

    const unsubscribe = subscribeToSettings(
      (latestSettings) => {
        const isStillActive = Boolean(
          latestSettings?.webMaintenance?.isEnabled ??
          latestSettings?.webMaintenance?.maintenanceEnabled ??
          (latestSettings as any)?.maintenanceEnabled
        );

        if (!isStillActive && onMaintenanceEnded) {
          onMaintenanceEnded(latestSettings);
        }
      },
      (error) => {
        console.warn('Listener de mantenimiento:', error);
      }
    );

    return () => {
      unsubscribe();
    };
  }, [isPreview, onMaintenanceEnded]);

  const title =
    maintenance?.title ||
    maintenance?.maintenanceTitle ||
    'Estamos realizando mejoras';

  const message =
    maintenance?.message ||
    maintenance?.maintenanceMessage ||
    'Volveremos pronto. Gracias por esperar.';

  return (
    <div
      id="web-maintenance-screen"
      role="status"
      aria-label="Sitio en mantenimiento"
      className="fixed inset-0 z-50 h-[100dvh] w-full bg-[#FFFDFB] text-[#302B30] flex flex-col items-center justify-center p-6 select-none font-['Plus_Jakarta_Sans',sans-serif] overflow-hidden"
      style={{
        paddingTop: 'max(1.5rem, env(safe-area-inset-top))',
        paddingBottom: 'max(1.5rem, env(safe-area-inset-bottom))',
        paddingLeft: 'max(1.5rem, env(safe-area-inset-left))',
        paddingRight: 'max(1.5rem, env(safe-area-inset-right))',
      }}
    >
      <div className="flex flex-col items-center text-center max-w-sm w-full mx-auto space-y-6 p-8 rounded-3xl bg-white border border-[#F0DDE4] shadow-[0_12px_36px_rgba(232,93,142,0.08)]">
        {/* 1. PEQUEÑO INDICADOR VISUAL DE MANTENIMIENTO */}
        <div
          aria-hidden="true"
          className="w-14 h-14 rounded-2xl bg-[#FFF4F7] border border-[#F0DDE4] flex items-center justify-center text-[#E83E78] shadow-2xs"
        >
          <Wrench className="w-6 h-6 text-[#E83E78]" />
        </div>

        {/* 2. TÍTULO */}
        <h1 className="text-2xl sm:text-3xl font-bold tracking-tight text-[#302B30] font-['Playfair_Display',serif] leading-snug">
          {title}
        </h1>

        {/* 3. MENSAJE CORTO */}
        <p className="text-sm text-[#77717A] font-medium leading-relaxed max-w-xs">
          {message}
        </p>

        {/* 4. INDICADOR DE ESTADO DISCRETO */}
        <div className="pt-2 flex items-center justify-center gap-2 text-xs text-[#77717A] font-semibold">
          <span
            aria-hidden="true"
            className="w-2 h-2 rounded-full bg-[#E83E78] animate-pulse motion-reduce:animate-none"
          />
          <span>Sitio temporalmente en preparación</span>
        </div>
      </div>
    </div>
  );
};
